"use client";

import React, { useState, useEffect, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebaseConfig';
import { doc, getDoc, collection, getDocs, updateDoc } from 'firebase/firestore';
import { XMarkIcon, ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { Bars3Icon } from '@heroicons/react/20/solid';
import { GiDominoMask, GiRadarSweep, GiSoundWaves, GiEchoRipples, GiSpeedometer } from "react-icons/gi";
import { MdContacts } from "react-icons/md";
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Cog6ToothIcon, UserCircleIcon, BellIcon, CreditCardIcon, ShieldCheckIcon, ExclamationTriangleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import usePageTitle from './utils/usePageTitle';
import SubscriptionManagementModal from './components/SubscriptionManagementModal';

// Function to filter navigation items based on current path
function getFilteredNavigation(pathname) {
  return [
    { name: 'Home', href: '/overview', icon: GiSpeedometer, current: pathname === '/overview' },
    { name: 'Relay', href: '/dashboard', icon: GiDominoMask, current: pathname === '/dashboard' },
    { name: 'Messages', href: '/dashboard/trace', icon: GiEchoRipples, current: pathname === '/dashboard/trace' },
    { name: 'World Call', href: '/world-call', icon: GiSoundWaves, current: pathname === '/world-call' },
    { name: 'Contacts', href: '/contacts', icon: MdContacts, current: pathname === '/contacts' },
    { name: 'Monitor', href: '/dashboard/monitor', icon: GiRadarSweep, current: pathname === '/dashboard/monitor' },
  ];
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Settings() {
  usePageTitle('Settings');

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('Account');
  const [userDoc, setUserDoc] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [showDeletionConfirm, setShowDeletionConfirm] = useState(false);
  const [showSubscriptionManagement, setShowSubscriptionManagement] = useState(false);
  const [hasActiveRelays, setHasActiveRelays] = useState(false);
  const [processingSubscription, setProcessingSubscription] = useState(false);
  const [showSetupAnimation, setShowSetupAnimation] = useState(false);
  const [setupMessage, setSetupMessage] = useState('');
  
  const setupMessages = [
    'Setting up your email aliases...',
    'Setting up phone numbers...',
    'Searching for identity leakage on the dark web...',
    'Scanning for data breaches...',
    'Configuring privacy shields...',
    'Establishing secure relay connections...',
    'Setting up message forwarding...',
    'Configuring spam filters...',
    'Setting up anonymous profiles...',
    'Preparing your secure dashboard...'
  ];
  
  const messageInterval = useRef(null);
  const setupTimeout = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const navigation = getFilteredNavigation(location.pathname);
  const settingsNavItem = { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, current: location.pathname === '/settings' };

  const secondaryNavigation = [
    { name: 'Account', href: '#account', icon: UserCircleIcon, current: activeTab === 'Account' },
    { name: 'Notifications', href: '#notifications', icon: BellIcon, current: activeTab === 'Notifications' },
    { name: 'Billing', href: '#billing', icon: CreditCardIcon, current: activeTab === 'Billing' },
    { name: 'Help', href: '#help', icon: QuestionMarkCircleIcon, current: activeTab === 'Help' },
  ];

  // Setup animation messages cycle
  useEffect(() => {
    if (showSetupAnimation) {
      let messageIndex = 0;
      
      // Set initial message
      setSetupMessage(setupMessages[0]);
      
      // Rotate messages every 3 seconds
      messageInterval.current = setInterval(() => {
        messageIndex = (messageIndex + 1) % setupMessages.length;
        setSetupMessage(setupMessages[messageIndex]);
      }, 3000);
    }
    
    return () => {
      if (messageInterval.current) {
        clearInterval(messageInterval.current);
      }
    };
  }, [showSetupAnimation]);

  // Fetch user data and subscription
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          setLoading(true);
          
          // Keep loading state true until we have determined what to show
          let shouldShowSetupAnimation = true;
          
          // Fetch user aliases
          const userAliasesRef = doc(db, 'userAliases', user.uid);
          const userAliasesDoc = await getDoc(userAliasesRef);
          
          if (userAliasesDoc.exists()) {
            const userData = userAliasesDoc.data();
            setUserDoc(userData);
            
            // Check if user has active relays
            const phoneAliasesRef = collection(db, 'userAliases', user.uid, 'phoneAliases');
            const phoneAliasesSnapshot = await getDocs(phoneAliasesRef);
            
          // Check for active email aliases (status === 'Active')
          const hasActiveEmail = userData.aliases && userData.aliases.some(alias => alias.status === 'Active');
          
          // Check for active phone aliases (connectionStatus === 'Active')
          let hasActivePhone = false;
          phoneAliasesSnapshot.forEach(doc => {
            const phoneData = doc.data();
            if (phoneData.connectionStatus === 'Active') {
              hasActivePhone = true;
            }
          });
            
            setHasActiveRelays(hasActiveEmail || hasActivePhone);
          }
          
          // Fetch subscription data
          const subscriptionRef = doc(db, 'userSubscriptions', user.uid);
          const subscriptionDoc = await getDoc(subscriptionRef);
          
          if (subscriptionDoc.exists()) {
            const subscriptionData = subscriptionDoc.data();
            setSubscription(subscriptionData);
            
            // Check if account is new (less than 1h55m old)
            if (subscriptionData.createdAt) {
              const createdAt = new Date(subscriptionData.createdAt);
              const now = new Date();
              const timeDiff = now - createdAt;
              const setupPeriod = 115 * 60 * 1000; // 1h55m in milliseconds
              
              // For development/testing purposes - check if we should bypass the animation
              const shouldBypassAnimation = new URLSearchParams(window.location.search).get('bypass_setup') === 'true';
              
              if (timeDiff >= setupPeriod || shouldBypassAnimation) {
                shouldShowSetupAnimation = false;
              }
            }
          }
          
          // Set the animation state after all data is loaded and processed
          setShowSetupAnimation(shouldShowSetupAnimation);
          
          // Only set loading to false after we've determined whether to show the animation
          setLoading(false);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setLoading(false);
          setShowSetupAnimation(false); // In case of error, don't block the UI
        }
      } else {
        // Redirect to login if not authenticated
        navigate('/');
      }
    });
    
    return () => {
      unsubscribe();
      if (setupTimeout.current) {
        clearTimeout(setupTimeout.current);
      }
    };
  }, [navigate]);

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
  };

  const handleManageSubscription = () => {
    setShowSubscriptionManagement(true);
  };

  const handleCancelSubscriptionRequest = () => {
    if (hasActiveRelays) {
      setShowCancelWarning(true);
    } else {
      setShowDeletionConfirm(true);
    }
  };

  const handleArchiveAccount = async () => {
    if (!auth.currentUser || processingSubscription) return;
    
    try {
      setProcessingSubscription(true);
      
      // 1. Cancel the current subscription
      const response = await fetch('/api/cancel-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          subscriptionId: subscription.subscriptionId,
          uid: auth.currentUser.uid
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }
      
      // 2. Create archive subscription using existing customer and price ID
      const createResponse = await fetch('/api/create-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: 'price_1RfWPDK5A174Olbc0O7849pg',
          customerId: subscription.stripeId,
          uid: auth.currentUser.uid
        }),
      });
      
      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(`Failed to create archive subscription: ${errorData.error}`);
      }
      
      // 3. Redirect to dashboard (API handles Firestore updates automatically)
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Error archiving account:', error);
      alert(`Failed to archive account: ${error.message}`);
    } finally {
      setProcessingSubscription(false);
      setShowDeletionConfirm(false);
    }
  };

  const handleDeleteAccount = () => {
    // Do nothing - just close the modal
      setShowDeletionConfirm(false);
  };

  const handleCancelSubscriptionCompletely = async () => {
    if (!auth.currentUser || processingSubscription) return;
    
    try {
      setProcessingSubscription(true);
      
      // Cancel the subscription completely (no archive)
      const response = await fetch('/api/cancel-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          subscriptionId: subscription.subscriptionId,
          uid: auth.currentUser.uid
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }
      
      // Redirect to a confirmation page or show success message
      alert('Your subscription has been canceled successfully.');
      navigate('/');
      
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert(`Failed to cancel subscription: ${error.message}`);
    } finally {
      setProcessingSubscription(false);
      setShowDeletionConfirm(false);
    }
  };

  return (
    <>
      {/* Mobile sidebar */}
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
        <DialogBackdrop transition className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0" />
          <div className="fixed inset-0 flex">
          <DialogPanel transition className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full">
              <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                    <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                  </button>
                </div>
              </TransitionChild>
            
            <div 
              className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl"
              style={{
                background: 'rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
              }}
            >
              <div className="flex h-16 shrink-0 items-center justify-center px-4">
                <img alt="Relay" src="/images/logowhite.png" className="h-8 w-auto" />
                    </div>
              <nav className="flex flex-1 flex-col px-4">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                    <ul role="list" className="space-y-3">
                          {navigation.map((item) => (
                            <li key={item.name}>
                          <Link
                            to={item.href}
                            className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200 hover:bg-white/5 rounded-lg"
                            onClick={() => setSidebarOpen(false)}
                          >
                            <item.icon 
                              aria-hidden="true" 
                                className={classNames(
                                "h-6 w-6 shrink-0 transition-colors duration-200",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                                )}
                            />
                            <span className={classNames(
                              "transition-colors duration-200",
                              item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                            )}>
                                {item.name}
                            </span>
                          </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                  <li className="mt-auto mb-4">
                    <Link
                      to={settingsNavItem.href}
                      className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200 hover:bg-white/5 rounded-lg"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <settingsNavItem.icon 
                        aria-hidden="true" 
                      className={classNames(
                          "h-6 w-6 shrink-0 transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )} 
                      />
                      <span className={classNames(
                        "transition-colors duration-200",
                        settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                      )}>
                      {settingsNavItem.name}
                      </span>
                    </Link>
                  </li>
                </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ${
        sidebarExpanded ? 'lg:w-72' : 'lg:w-20'
      }`}>
        <div className="relative">
          <div 
            className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl relative"
            style={{
              background: 'rgba(0, 0, 0, 0.2)',
              backdropFilter: 'blur(20px)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)',
              height: 'calc(100vh - 2rem)'
            }}
          >
            <div className={`flex h-16 shrink-0 items-center justify-center transition-all duration-300 ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
              <img
                alt="Relay"
                src="/images/logowhite.png"
                className={`transition-all duration-300 ${sidebarExpanded ? 'h-8 w-auto' : 'h-10 w-auto max-w-none'}`}
                style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.1))' }}
              />
              </div>

            <nav className={`flex flex-1 flex-col ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="space-y-3">
                {navigation.map((item) => (
                  <li key={item.name}>
                        <Link
                          to={item.href}
                          className={classNames(
                            'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg hover:bg-white/5',
                            sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                          )}
                          title={!sidebarExpanded ? item.name : undefined}
                        >
                          <item.icon 
                            aria-hidden="true" 
                            className={classNames(
                              "h-6 w-6 shrink-0 transition-colors duration-200",
                              item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                            )} 
                          />
                          {sidebarExpanded && (
                            <span className={classNames(
                              "transition-opacity duration-300",
                              item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                            )}>
                              {item.name}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
                <li className="mt-auto mb-4">
                  <Link
                    to={settingsNavItem.href}
                    className={classNames(
                      'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg hover:bg-white/5',
                      sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                    )}
                    title={!sidebarExpanded ? settingsNavItem.name : undefined}
                  >
                    <settingsNavItem.icon 
                      aria-hidden="true" 
                      className={classNames(
                        "h-6 w-6 shrink-0 transition-colors duration-200",
                        settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                      )} 
                    />
                    {sidebarExpanded && (
                      <span className={classNames(
                        "transition-opacity duration-300",
                        settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                      )}>
                        {settingsNavItem.name}
                      </span>
                    )}
                  </Link>
                  </li>
              </ul>
            </nav>
            </div>

          {/* Right border expand/collapse area */}
          <div 
            className="absolute top-0 right-0 w-4 h-full cursor-pointer group flex items-center justify-center"
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
            style={{ zIndex: 60 }}
          >
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/10 backdrop-blur-md rounded-full p-1 border border-white/20">
              {sidebarExpanded ? (
                <ChevronLeftIcon className="h-4 w-4 text-white/70" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-white/70" />
              )}
            </div>
          </div>
        </div>
            </div>

      {/* Main content */}
      <div 
        className={`flex-1 min-h-screen transition-all duration-300 ${
          sidebarExpanded ? 'lg:pl-80' : 'lg:pl-28'
        }`}
        style={{background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'}}
      >
        {/* Mobile header */}
        <button 
          type="button" 
          onClick={() => setSidebarOpen(true)} 
          className="fixed top-4 left-4 z-50 lg:hidden bg-white/10 backdrop-blur-md rounded-full p-3 text-white hover:bg-white/20 transition-all border border-white/20"
          style={{
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'
          }}
        >
          <Bars3Icon aria-hidden="true" className="h-6 w-6" />
        </button>

        <div className="px-4 py-6 lg:px-8">
              {/* Initial loading state */}
              {loading ? (
            <div className="flex flex-col items-center justify-center min-h-[80vh]">
              <div 
                className="bg-black/20 backdrop-blur-2xl rounded-3xl border border-white/10 p-12 text-center"
                style={{
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
                }}
              >
                {/* Logo */}
                <div className="mb-8">
                  <img
                    alt="Relay"
                    src="/images/logowhite.png"
                    className="h-12 w-auto mx-auto"
                    style={{ filter: 'drop-shadow(0 0 12px rgba(255,255,255,0.2))' }}
                  />
                </div>
                
                {/* Loading animation */}
                <div className="mb-6">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
                
                {/* Loading text */}
                <div className="text-white/80 text-lg font-light">
                  Loading your account...
                </div>
              </div>
                </div>
              ) : showSetupAnimation ? (
                /* Show setup animation if account is new */
            <div className="flex flex-col items-center justify-center min-h-[80vh]">
              <div 
                className="bg-black/20 backdrop-blur-2xl rounded-3xl border border-white/10 p-12 text-center max-w-lg"
                style={{
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
                }}
              >
                {/* Logo */}
                <div className="mb-8">
                  <img
                    alt="Relay"
                    src="/images/logowhite.png"
                    className="h-12 w-auto mx-auto"
                    style={{ filter: 'drop-shadow(0 0 12px rgba(255,255,255,0.2))' }}
                  />
                </div>
                
                {/* Setup animation */}
                <div className="mb-8">
                  <div className="relative w-20 h-20 mx-auto">
                    <div className="absolute inset-0 border-4 border-blue-500/30 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-t-blue-500 rounded-full animate-spin"></div>
                  </div>
                </div>
                
                {/* Setup text */}
                <div className="text-white/80 text-xl font-light mb-4">
                  Setting up your account
                </div>
                <div className="text-blue-400 text-lg mb-8 animate-pulse font-medium">
                  {setupMessage}
                </div>
                <div className="text-white/60 text-sm leading-relaxed">
                    Your account was just created. We're setting up your privacy protection features.
                    This may take some time to complete.
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="mb-8">
                <div className="text-center lg:text-left">
                  <h1 className="text-4xl font-light text-white mb-2">
                    Settings
                  </h1>
                  <p className="text-xl text-white/60">
                    Manage your account, preferences, and billing
                  </p>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="mb-8">
                {/* Desktop Tab Navigation */}
                <div className="hidden sm:block bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-2"
                     style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
                  <nav className="flex space-x-2" aria-label="Tabs">
                    {secondaryNavigation.map((tab) => (
                      <button
                        key={tab.name}
                        onClick={() => handleTabChange(tab.name)}
                        className={classNames(
                          'flex items-center gap-x-2 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200',
                          tab.current
                            ? 'bg-white/10 text-white shadow-lg'
                            : 'text-white/60 hover:bg-white/5 hover:text-white/80'
                        )}
                      >
                        <tab.icon className="h-4 w-4" />
                        {tab.name}
                      </button>
                        ))}
                    </nav>
                </div>

                {/* Mobile Tab Navigation - Scrollable */}
                <div className="sm:hidden bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-2"
                     style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
                  <nav className="flex space-x-2 overflow-x-auto scrollbar-hide pb-1" aria-label="Tabs">
                    {secondaryNavigation.map((tab) => (
                      <button
                        key={tab.name}
                        onClick={() => handleTabChange(tab.name)}
                        className={classNames(
                          'flex-shrink-0 flex items-center gap-x-2 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 whitespace-nowrap min-w-fit',
                          tab.current
                            ? 'bg-white/10 text-white shadow-lg'
                            : 'text-white/60 hover:bg-white/5 hover:text-white/80'
                        )}
                      >
                        <tab.icon className="h-4 w-4" />
                        {tab.name}
                      </button>
                        ))}
                    </nav>
                  
                  {/* Mobile scroll indicator */}
                  <div className="flex justify-center mt-2">
                    <div className="text-white/40 text-xs">
                      ← Swipe to see all sections →
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Areas */}
              <div className="space-y-8">
                    {/* Account Tab */}
                    {activeTab === 'Account' && (
                  <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 overflow-hidden"
                       style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
                    <div className="p-6 border-b border-white/10">
                      <h2 className="text-xl font-light text-white mb-2">Personal Information</h2>
                      <p className="text-white/60">
                            Your account information and contact details.
                          </p>
                        </div>

                    <div className="p-6">
                      <div className="grid grid-cols-1 gap-6 max-w-2xl">
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                                  Email address
                                </label>
                                  <input
                                    id="email"
                                    name="email"
                                    type="email"
                            value={userDoc?.email || ''}
                                    readOnly
                            className="block w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm px-4 py-3 text-white placeholder-white/40 focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10 transition-all duration-200"
                                  />
                              </div>

                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-white mb-2">
                                  Phone number
                                </label>
                                  <input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    value={userDoc?.forwardToNumber || ''}
                                    readOnly
                            className="block w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm px-4 py-3 text-white placeholder-white/40 focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10 transition-all duration-200"
                                  />
                                </div>
                              </div>
                        </div>
                      </div>
                    )}

                    {/* Notifications Tab */}
                    {activeTab === 'Notifications' && (
                  <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 overflow-hidden"
                       style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
                    <div className="p-6 border-b border-white/10">
                      <h2 className="text-xl font-light text-white mb-2">Notifications</h2>
                      <p className="text-white/60">
                            Manage how you receive notifications.
                          </p>
                        </div>

                    <div className="p-6">
                      <div className="text-center py-12">
                        <BellIcon className="h-12 w-12 text-white/40 mx-auto mb-4" />
                        <p className="text-white/60">
                              Notification settings will be available in a future update.
                            </p>
                      </div>
                        </div>
                      </div>
                    )}

                    {/* Billing Tab */}
                    {activeTab === 'Billing' && (
                  <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 overflow-hidden"
                       style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
                    <div className="p-6 border-b border-white/10">
                      <h2 className="text-xl font-light text-white mb-2">Billing</h2>
                      <p className="text-white/60">
                            Manage your subscription and billing information.
                          </p>
                        </div>

                    <div className="p-6">
                      {userDoc?.isPremium ? (
                        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                          <div className="flex items-center gap-x-3 mb-4">
                            <div className="flex items-center justify-center w-8 h-8 bg-green-500/20 rounded-lg">
                              <ShieldCheckIcon className="h-5 w-5 text-green-400" />
                            </div>
                            <h3 className="text-lg font-medium text-white">Current Plan</h3>
                          </div>
                          <p className="text-white text-2xl font-light mb-6">
                                    {subscription?.bundleName || 
                                     (subscription?.isMessaging === true || subscription?.messagingPlanActive === true 
                                       ? 'Premium + Messaging' 
                                       : 'Relay Premium')
                                    } {subscription?.displayPrice || '$4.99/mo'}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={handleManageSubscription}
                            className="inline-flex items-center px-4 py-2 bg-indigo-600/80 backdrop-blur-sm text-white rounded-xl hover:bg-indigo-600 transition-all duration-200 border border-indigo-500/30"
                                  >
                                    Manage subscription
                                  </button>
                                </div>
                      ) : (
                        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                          <div className="flex items-center gap-x-3 mb-4">
                            <div className="flex items-center justify-center w-8 h-8 bg-blue-500/20 rounded-lg">
                              <CreditCardIcon className="h-5 w-5 text-blue-400" />
                            </div>
                            <h3 className="text-lg font-medium text-white">Current Plan</h3>
                          </div>
                          <p className="text-white text-2xl font-light">
                                    {userDoc?.isArchive ? 'Archive Plan $1.99/mo' : 'Free Plan'}
                                  </p>
                                </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Help Tab */}
                    {activeTab === 'Help' && (
                      <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 overflow-hidden"
                           style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
                        <div className="p-6 border-b border-white/10">
                          <h2 className="text-xl font-light text-white mb-2">Help & Support</h2>
                          <p className="text-white/60">
                            Get help and find answers to your questions.
                          </p>
                        </div>

                        <div className="p-6">
                          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                            <div className="flex items-center gap-x-3 mb-4">
                              <div className="flex items-center justify-center w-8 h-8 bg-blue-500/20 rounded-lg">
                                <QuestionMarkCircleIcon className="h-5 w-5 text-blue-400" />
                              </div>
                              <h3 className="text-lg font-medium text-white">Need Help?</h3>
                            </div>
                            <p className="text-white/60 mb-6">
                              Visit our comprehensive help center for guides, tutorials, and answers to frequently asked questions.
                            </p>
                            <a
                              href="https://help.chatodyssey.com/en"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-6 py-3 bg-indigo-600/80 backdrop-blur-sm text-white rounded-xl hover:bg-indigo-600 transition-all duration-200 border border-indigo-500/30 hover:scale-105 transform"
                              style={{
                                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(79, 70, 229, 0.3)'
                              }}
                            >
                              <QuestionMarkCircleIcon className="h-5 w-5 mr-2" />
                              Visit Help Center
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
        </div>
      </div>

      {/* Warning Modal for Active Relays */}
      {showCancelWarning && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50 p-4" 
          style={{background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)'}}
        >
          <div 
            className="rounded-2xl max-w-md w-full p-6 border border-white/10 backdrop-blur-2xl"
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
            }}
          >
            <div className="flex items-center gap-x-3 mb-4">
              <div className="flex items-center justify-center w-8 h-8 bg-amber-500/20 rounded-lg">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" />
              </div>
              <h3 className="text-xl font-medium text-white">Active Relays Detected</h3>
            </div>
            <p className="text-white/80 mb-6">
              You must deactivate your email and phone aliases in order to manage your subscription. You can do so on the dashboard.
            </p>
            <div className="flex justify-end">
              <Link
                to="/dashboard"
                className="inline-flex items-center px-4 py-2 bg-indigo-600/80 backdrop-blur-sm text-white rounded-xl hover:bg-indigo-600 transition-all duration-200 border border-indigo-500/30"
              >
                Go to Dashboard
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Cancellation/Archive Modal */}
      {showDeletionConfirm && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50 p-4" 
          style={{background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)'}}
        >
                     <div 
             className="rounded-2xl max-w-md w-full p-6 border border-white/10 backdrop-blur-2xl relative"
             style={{
               background: 'rgba(0, 0, 0, 0.3)',
               boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
             }}
           >
             {/* Close button */}
             <button
               onClick={() => setShowDeletionConfirm(false)}
               className="absolute top-4 right-4 p-1 rounded-lg hover:bg-white/10 transition-colors duration-200"
             >
               <XMarkIcon className="h-5 w-5 text-white/60 hover:text-white/80" />
             </button>
             
             <div className="flex items-center gap-x-3 mb-4">
               <div className="flex items-center justify-center w-8 h-8 bg-red-500/20 rounded-lg">
                 <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
               </div>
               <h3 className="text-xl font-medium text-white">Manage Your Subscription</h3>
             </div>
                         <p className="text-white/80 mb-6 text-sm">
              Unsubscribing will permanently delete all your phone numbers, email aliases, and text message records. 
              This means you could lose access to services that rely on them. Want to keep your data just in case? 
               Downgrade to our Archive plan instead, for $1.99/mo for 12 months.
            </p>
                         <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCancelSubscriptionCompletely}
                disabled={processingSubscription}
                className="px-3 py-2 bg-white/5 backdrop-blur-sm text-white/60 text-xs rounded-lg hover:bg-white/10 hover:text-white/80 transition-all duration-200 border border-white/10"
              >
                Cancel Subscription
              </button>
              <button
                onClick={handleArchiveAccount}
                disabled={processingSubscription}
                 className="flex-1 px-4 py-2 bg-indigo-600/80 backdrop-blur-sm text-white rounded-xl hover:bg-indigo-600 transition-all duration-200 border border-indigo-500/30 disabled:opacity-50"
              >
                {processingSubscription ? 'Processing...' : 'Archive my account'}
               </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Management Modal */}
      <SubscriptionManagementModal
        isOpen={showSubscriptionManagement}
        onClose={() => setShowSubscriptionManagement(false)}
        subscription={subscription}
        onCancelSubscription={handleCancelSubscriptionRequest}
      />
    </>
  );
} 